import {Injectable} from '@angular/core';
import {DomSanitizer, SafeHtml} from '@angular/platform-browser';
import {LayerUtilService} from './layer-util.service';
import {LookupService} from '../../common/service/lookup.service';
import {SpatialUtilService} from '../services/spatial-util.service';
import {LineSymbologyLookup} from '../../common/models/line-symbology.model';

/* eslint-disable @typescript-eslint/no-explicit-any */
@Injectable({
    providedIn: 'root'
})
export class ConstructLineLayerService {
    private readonly layerId = 'constructLineFeatureLayer_layer';

    private readonly layerSelector = `#${this.layerId}`;

    private readonly featureClass = 'constructline';

    private readonly featureIdAttrName = this.layerUtilService.getFeatureIdAttrName(this.featureClass);

    private readonly featureTypeAttrName = this.layerUtilService.getFeatureTypeAttrName(this.featureClass);

    private readonly lineSymbologyLookups: LineSymbologyLookup[] = this.lookupService
        .getLookups('lineSymbology')
        .filter((l: LineSymbologyLookup) => l.type === 96);

    private svgStyle: string;

    private trustedSvgStyle: SafeHtml;

    constructor(
        private domSanitizer: DomSanitizer,
        private layerUtilService: LayerUtilService,
        private lookupService: LookupService,
        private spatialUtilService: SpatialUtilService
    ) {}

    getSvgStyle(): SafeHtml {
        const scaleFactor = this.layerUtilService.getPixelScaleFactor();
        if (scaleFactor === 0) {
            return '';
        }

        const strokeWidthScaleFactor = this.layerUtilService.getStrokeWidthPixelScaleFactor(scaleFactor);

        const mapBackgroundColor = this.spatialUtilService.getMapBackground();

        const showFeatures = this.layerUtilService.showFeatures();

        const newSvgStyle = [
            `${this.layerSelector} > path[${this.featureTypeAttrName}]:hover` +
                ` + g {animation: feature-symbol-hover 0.3s ease-in-out alternate none infinite;}`,

            `${this.layerSelector} > path[${this.featureTypeAttrName}]:not([data-hidden]):not([data-selected]):hover` +
                ` + g > path:first-of-type {stroke:${this.layerUtilService.getHoverColor()};}`,

            `${this.layerSelector} > path[${this.featureTypeAttrName}][data-selected]` +
                ` + g > path:first-of-type {stroke:${this.layerUtilService.getSelectedColor()};}`,

            `${this.layerSelector} > path[${this.featureTypeAttrName}][data-hidden]:not([data-selected])` +
                ` + g > path:first-of-type {stroke:${this.layerUtilService.getHiddenColor()};}`,

            ...this.lineSymbologyLookups
                .filter(l => l.viewMode.toLowerCase() === mapBackgroundColor)
                .map(l => {
                    const stroke = showFeatures ? `rgb(${l.color})` : 'transparent';
                    const strokeLineCap = l.lineCap;
                    const strokeLineJoin = l.lineJoin;

                    const pointerEventsLineStrokeWidth =
                        l.width * this.layerUtilService.getPointerEventsLineWidthPixelScaleFactor(strokeWidthScaleFactor);

                    const pointerEventsLineProperties = [
                        `stroke:${stroke};`,
                        `stroke-linecap:${strokeLineCap};`,
                        `stroke-linejoin:${strokeLineJoin};`,
                        `stroke-miterlimit:1;`,
                        `stroke-width: ${pointerEventsLineStrokeWidth};`,
                        `stroke-opacity:0.0;`,
                        `fill-opacity:1.0;`
                    ];

                    let strokeDashArray: string;

                    if (l.dasharray) {
                        const delimiter = parseFloat(l.delimiter);
                        const dashValueMultiplier = scaleFactor * delimiter;
                        strokeDashArray = l.dasharray
                            .split(',')
                            .map(v => `${parseFloat(v) * dashValueMultiplier}`)
                            .join();
                    } else {
                        strokeDashArray = 'none';
                    }

                    const dashLineStrokeWidth = l.width * strokeWidthScaleFactor;

                    const dashLineProperties = [
                        `stroke:${stroke};`,
                        `stroke-linecap:${strokeLineCap};`,
                        `stroke-linejoin:${strokeLineJoin};`,
                        `stroke-miterlimit:1;`,
                        `stroke-width: ${dashLineStrokeWidth};`,
                        `stroke-opacity:1.0;`,
                        `fill-opacity:1.0;`,
                        `stroke-dasharray: ${strokeDashArray};`,
                        `pointer-events: none;`
                    ];

                    const styles = [
                        `${this.layerSelector} > path[${this.featureTypeAttrName}="${l.type}"] {${pointerEventsLineProperties.join('')}}`,
                        `${this.layerSelector} > path[${this.featureTypeAttrName}="${l.type}"]` +
                            ` + g > path:first-of-type {${dashLineProperties.join('')}}`
                    ];

                    return styles.join('\n');
                })
        ].join('\n');

        if (newSvgStyle === this.svgStyle) {
            return this.trustedSvgStyle;
        }

        this.svgStyle = newSvgStyle;

        this.trustedSvgStyle = this.domSanitizer.bypassSecurityTrustHtml(this.svgStyle);

        return this.trustedSvgStyle;
    }

    init() {
        const constructLineFeatureLayer = this.spatialUtilService.getConstructLineMapLayer();

        this.layerUtilService.addLayerEventHandler(constructLineFeatureLayer, 'graphic-node-add', (evt: {graphic: any; node: any}) => {
            const lineSymbologyLookup = this.getLineSymbologyLookup(evt.graphic.attributes.type);
            if (!lineSymbologyLookup) {
                evt.node.parentNode.removeChild(evt.node);
                return;
            }

            const featureId = evt.graphic.attributes.id;

            document
                .querySelectorAll(`${this.layerSelector} [${this.featureIdAttrName}="${featureId}"]`)
                .forEach(node => node.parentNode.removeChild(node));

            evt.node.setAttribute(this.featureTypeAttrName, `${evt.graphic.attributes.type}`);
            evt.node.setAttribute(this.featureIdAttrName, `${featureId}`);
            this.layerUtilService.setSelectedAttr(evt.node, this.featureClass, featureId);

            const lineLayerNode = document.getElementById(this.layerId);

            document.getElementById(this.layerId);

            const g: any = document.createElementNS('http://www.w3.org/2000/svg', 'g');
            g.setAttribute('id', this.layerUtilService.getFeatureNodeId(this.featureClass, featureId));
            g.setAttribute(this.featureIdAttrName, `${featureId}`);
            lineLayerNode.insertBefore(g, evt.node.nextSibling);

            const dashPath = document.createElementNS('http://www.w3.org/2000/svg', 'path');
            this.updateDashPath(evt, dashPath);

            g.appendChild(dashPath);
        });

        this.layerUtilService.addLayerEventHandler(constructLineFeatureLayer, 'graphic-node-remove', (evt: any) => {
            const featureId = evt.graphic.attributes.id;
            const g: any = document.querySelector(`${this.layerSelector} > g[${this.featureIdAttrName}="${featureId}"]`);
            if (g) {
                g.parentNode.removeChild(g);
            }
        });

        this.layerUtilService.addLayerEventHandler(constructLineFeatureLayer, 'graphic-draw', (evt: any) => {
            const featureId = evt.graphic.attributes.id;

            this.layerUtilService.setHiddenAttr(evt.node, this.featureClass, featureId);

            this.updateDashPath(
                evt,
                document.querySelector(`${this.layerSelector} > g[${this.featureIdAttrName}="${featureId}"] > path:first-of-type`)
            );
        });

        this.layerUtilService.addLayerEventHandler(constructLineFeatureLayer, 'edits-complete', () => {
            const {lineIds, lineTypes} = this.spatialUtilService.getConstructLineGraphics().reduce(
                (acc: any, g: any) => {
                    acc.lineIds.push(g.attributes.id);
                    acc.lineTypes.push(g.attributes.type);
                    return acc;
                },
                {lineIds: [], lineTypes: []}
            );

            Array.from(document.querySelectorAll(`${this.layerSelector} > [${this.featureIdAttrName}]`))
                .map(v => parseInt(v.getAttribute(this.featureIdAttrName)))
                .filter((v, i, a) => i === a.indexOf(v))
                .forEach(featureId => {
                    const lineIdx = lineIds.indexOf(featureId);
                    const path = document.querySelector(`${this.layerSelector} > path[${this.featureIdAttrName}="${featureId}"]`);

                    if (lineIdx === -1) {
                        const g = document.querySelector(`${this.layerSelector} > g[${this.featureIdAttrName}="${featureId}"]`);
                        if (g) {
                            g.parentNode.removeChild(g);
                        }
                        if (path) {
                            path.parentNode.removeChild(path);
                        }
                    } else if (path) {
                        const lineType = lineTypes[lineIdx];
                        const lineTypeAttrValue = parseInt(path.getAttribute(this.featureTypeAttrName));
                        if (lineTypeAttrValue !== lineType) {
                            path.setAttribute(this.featureTypeAttrName, `${lineType}`);
                        }
                    }
                });
        });

        this.layerUtilService.addLayerEventHandler(constructLineFeatureLayer, 'click', (evt: any) => {
            this.layerUtilService.onClick(this.featureClass, evt);
        });

        this.layerUtilService.addLayerEventHandler(constructLineFeatureLayer, 'dbl-click', (evt: any) => {
            this.layerUtilService.setSelectedAttr(evt.target, this.featureClass, evt.graphic.attributes.id);
        });

        this.layerUtilService.addLayerEventHandler(constructLineFeatureLayer, 'mouse-over', (evt: any) => {
            this.layerUtilService.onMouseOver(this.featureClass, evt);
        });

        this.layerUtilService.addLayerEventHandler(constructLineFeatureLayer, 'mouse-out', (evt: any) => {
            this.layerUtilService.onMouseOut(this.featureClass, evt);
        });

        return constructLineFeatureLayer;
    }

    private getLineSymbologyLookup(lineType: number) {
        const lookupViewMode = this.spatialUtilService.getMapBackground() === 'white' ? 'White' : 'Black';
        return this.lineSymbologyLookups.find((l: LineSymbologyLookup) => l.type === lineType && l.viewMode === lookupViewMode);
    }

    private getPathCoordinates(path: any) {
        return path.getAttribute('path').slice(2).replace(/\s/g, ',').split(',').map(parseFloat);
    }

    private updateDashPath(evt: any, dashPath: any) {
        if (!dashPath) {
            return;
        }

        const coordinates = this.getPathCoordinates(evt.node);
        dashPath.setAttribute('d', `M ${coordinates[0]} ${coordinates[1]} ${coordinates[2]} ${coordinates[3]}`);
    }
}
